import {
  AfterContentInit,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import { NzModalService } from 'ng-zorro-antd/modal';

import { AuthService, NotificationService } from '../../../../core/services';
import { NotificationComponent } from '../../../../shared/components';

import { MainDashboardInterface, urlVastAttributes } from '../../models';
import { MacrosListComponent } from '../macros-list';

interface MacrosItem {
  showAttr: number; // Assuming showAttr is a number based on your usage
  macros: string;
  shortcut: string;
  mobileWeb: boolean;
  desktop: boolean;
  inApp: boolean;
  ctv: boolean;
  // Add other properties as needed
}


@Component({
  selector: 'app-vast-endpoint',
  templateUrl: './vast-endpoint.component.html',
  styleUrls: ['./vast-endpoint.component.scss'],
  standalone: false
})
export class VastEndpointComponent implements AfterContentInit {

  @Input() public set connection(value: { bidfloor: any; macrosList: string[]; }) {
    this._connection = value;
  }

  public get connection(): { bidfloor: any; macrosList: string[]; } {
    return this._connection;
  }

  @Input() public set partnerNs(value: string) {
    this._partnerNs = value;
  }

  public get partnerNs(): string {
    return this._partnerNs;
  }

  @Input() public set sizeRequired(value: boolean) {
    this._sizeRequired = value;
  }

  public get sizeRequired(): boolean {
    return this._sizeRequired;
  }

  @Input() public set connectionType(value: string) {
    this._connectionType = value;
  }

  public get connectionType(): string {
    return this._connectionType;
  }

  @Input() public set adServer(value: string | number) {
    this._adServer = value;
  }

  public get adServer(): string | number {
    return this._adServer;
  }

  @Input() public set adServerMacroses(value: MacrosItem[]) {
    this._adServerMacroses = value;
  }

  public get adServerMacroses(): MacrosItem[] {
    return this._adServerMacroses;
  }

  @Input() public set adtype(value: string) {
    this._adtype = value;
  }

  public get adtype(): string {
    return this._adtype;
  }

  @Input() public set seat(value: string) {
    this._seat = value;
  }

  public get seat(): string {
    return this._seat;
  }

  @Input() public set token(value: string) {
    this._token = value;
  }

  public get token(): string {
    return this._token;
  }

  @Input() public set protocol(value: string) {
    this._protocol = value;
  }

  public get protocol(): string {
    return this._protocol;
  }

  @Input() public set name(value: string) {
    this._name = value;
  }

  public get name(): string {
    return this._name;
  }

  @Input() public set selectedMacrosList(value: string[]) {
    this._selectedMacrosList = value;

    this.forSspEndpoint();
  }

  public get selectedMacrosList(): string[] {
    return this._selectedMacrosList;
  }

  @Input() public set listOfMacros(value: any[]) {
    this._listOfMacros = value;
  }

  public get listOfMacros(): any[] {
    return this._listOfMacros;
  }

  @Input() public set macrosListVast(value: MainDashboardInterface.IVastMacros[]) {
    this._macrosListVast = value;
  }

  public get macrosListVast(): MainDashboardInterface.IVastMacros[] {
    return this._macrosListVast;
  }

  @Output() public changeVastEndpoint = new EventEmitter<string>();

  @Output() public changeVastMacrosList = new EventEmitter<string[]>();

  protected endpointUrl = '';

  private macrosListContentHeight = 'auto';
  private macrosListBodyHeight = 'auto';
  private urlVastAttributes = urlVastAttributes;

  private _connection: { bidfloor: any; macrosList: any[]; } = {
    bidfloor: 0,
    macrosList: [],
  };
  private _partnerNs: string;
  private _sizeRequired = false;
  private _connectionType = 'vast';
  private _adServer: string | number;
  private _adServerMacroses: MacrosItem[];
  private _adtype: string;
  private _seat: string;
  private _token: string;
  private _protocol: string;
  private _name: string;
  private _selectedMacrosList: string[] = [];
  private _listOfMacros: any[] = [];
  private _macrosListVast: MainDashboardInterface.IVastMacros[] = [];

  constructor(
    private authService: AuthService,
    private modalService: NzModalService,
    private notificationService: NotificationService,
    private notificationComponent: NotificationComponent,
  ) { }

  public ngAfterContentInit(): void {
    this.macrosListContentHeight = window.innerHeight - 200 + 'px';
    this.macrosListBodyHeight = window.innerHeight - 205 + 'px';
    window.addEventListener('resize', () => {
      this.macrosListContentHeight = window.innerHeight - 200 + 'px';
      this.macrosListBodyHeight = window.innerHeight - 205 + 'px';
    });
  }

  protected linkCopy() {
    this.showNotification('Endpoint Copied');
  }

  protected changeMacros(selectedItems: string[]) {
    const attributes = this.getUrlAttributes();
    let url = this.buildBaseUrl();

    if (this.adServer === null) {
      url += this.appendDefaultMacros();

      selectedItems.forEach(item => {
        url += this.appendItem(attributes, item);
      });
    } else {
      selectedItems.forEach(item => {
        url += this.appendAdServerMacros(attributes, item);
      });
    }

    this.updateEndpointAndEmitChanges(url, selectedItems);
  }

  protected openMacros() {
    this.modalService.create({
      nzContent: MacrosListComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'macros-list-modal',
      nzWrapClassName: 'macros-list-modal-wrap',
      nzStyle: { top: '30px', width: '650px', height: this.macrosListContentHeight },
      nzBodyStyle: { width: '650px', padding: '0px', height: this.macrosListBodyHeight, overflowY: 'scroll' },
      nzData: {
        macrosList: this.listOfMacros,
      }
    });
    return false;
  }

  private buildBaseUrl(): string {
    return `${this.protocol}${this.partnerNs}/?seat=${this.seat}&token=${this.token}`;
  }

  private appendDefaultMacros(): string {
    return '&cb=[CB]&ua=[UA]&ip=[IP]&w=[WIDTH]&h=[HEIGHT]';
  }

  private appendItem(attributes: any, item: string): string {
    return `&${attributes[item]}=${item}`;
  }

  private appendAdServerMacros(attributes: any, item: string): string {
    let attrAndMacros = `&${attributes[item]}=${item}`;
    this.adServerMacroses.forEach(macro => {
      if (item === macro.shortcut && macro.showAttr === 1) {
        attrAndMacros = `&${macro.shortcut}=${macro.macros}`;
      }
    });
    return attrAndMacros;
  }

  private updateEndpointAndEmitChanges(url: string, selectedItems: string[]): void {
    this.selectedMacrosList = selectedItems;
    this.endpointUrl = url;

    this.changeVastEndpoint.emit(this.endpointUrl);
    this.changeVastMacrosList.emit(this.selectedMacrosList);
  }

  private setOriginalEndpointUrl() {
    if (this.adServer === null) {
      return this.token + '&cb=[CB]&ua=[UA]&ip=[IP]&w=[WIDTH]&h=[HEIGHT]';
    } else {
      return this.token;
    }
  }

  private forSspEndpoint() {
    const protocol = this.authService.partner.useSsl ? 'https' : 'http';
    this.endpointUrl = `${protocol}://${this.partnerNs}/?seat=${this.seat}&token=${this.setOriginalEndpointUrl()}`;

    if (this.adServer !== null) {
      this.appendAdServerMacroses();
    } else if (this.connectionType === 'vast') {
      this.appendVastMacroses();
    }

    this.changeVastEndpoint.emit(this.endpointUrl);
  }

  private appendAdServerMacroses(): void {
    this.adServerMacroses.forEach(macro => {
      this.endpointUrl += `&${macro.shortcut}=${macro.macros}`;
    });
  }

  private appendVastMacroses(): void {
    this.selectedMacrosList.forEach(item => {
      this.endpointUrl += `&${this.urlVastAttributes[item]}=${item}`;
    });
  }

  private getUrlAttributes() {
    return this.urlVastAttributes;
  }

  private showNotification(description: string): void {
    const data = {
      iconType: 'info',
      color: 'info',
      notificationType: 'info',
      description,
    };

    this.notificationComponent.data = data;
    this.notificationService.showTemplate({ nzData: data, nzStyle: { width: '300px' } });
  }

}
